import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui";
import { File, FileText, Hash, HelpCircle, List, Upload } from "lucide-react";

import { AiBubble, UserBubble } from "@/components/chat/ChatBubble";
import { SummaryProgressIndicator, ThinkingIndicator } from "@/components/onboarding/ThinkingIndicator";
import type { TMessage } from "@/types/chat";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";

// Icon mapping for answer types
const AnswerTypeIcon = ({ type }: { type: string }) => {
  const iconClass = "h-3.5 w-3.5";
  switch (type) {
    case "number":
      return <Hash className={iconClass} />;
    case "multiple_choice":
      return <List className={iconClass} />;
    case "file_upload":
    case "file_with_text":
      return <Upload className={iconClass} />;
    default:
      return <FileText className={iconClass} />;
  }
};

// Strip legacy "(Uploaded file: "name")" text that was stored in old messages before the file chip approach
function stripLegacyFileText(text: string): string {
  return text.replace(/\(Uploaded file:\s*"[^"]*"\)\n?/g, "").trim();
}

// Safe markdown renderer component
const SafeMarkdown = ({ content }: { content: string }) => {
  if (!content || typeof content !== "string") {
    return <span>{String(content || "")}</span>;
  }
  return <ReactMarkdown>{stripLegacyFileText(content)}</ReactMarkdown>;
};

type TOnboardingMessageListProps = {
  messages: TMessage[];
};

export default function OnboardingMessageList({ messages }: TOnboardingMessageListProps) {
  const containerClassName = useMemo(() => {
    return "p-4 md:p-6 min-h-full";
  }, []);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-4 text-muted-foreground">
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  // Display-only file chip — no click/open until in-page viewer is implemented
  const renderFileLink = (file: { id: string; name: string; url?: string }) => {
    return (
      <div className="mt-2">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted px-2.5 py-1.5 text-xs font-medium text-foreground">
          <File className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          {file.name}
        </span>
      </div>
    );
  };

  const renderMessages = () =>
    messages.map((message) => {
      return (
        <div
          key={message.id}
          className={`mb-6 flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
        >
          {message.sender === "ai" && (
            <AiBubble kind={message.kind}>
              <>
                {/* Text and indicator are additive: a message that carries both (e.g. the
                    closing onboarding turn) must not have its text swallowed by a spinner. */}
                {message.text && (
                  <div className="markdown-content">
                    <SafeMarkdown content={message.text} />
                  </div>
                )}

                <div className={message.text ? "mt-3" : undefined}>
                  {message.kind === "progress" ? (
                    <SummaryProgressIndicator
                      stage={message.progressStage}
                      elapsedSeconds={message.progressElapsedSeconds}
                    />
                  ) : (
                    message.thinking && (
                      <ThinkingIndicator variant={message.id === "ai-initial-thinking" ? "initial" : "response"} />
                    )
                  )}
                </div>

                {message.kind !== "progress" && (
                  <>
                    {/* Question description - displayed separately */}
                    {message.description && <p className="text-xs text-muted-foreground">{message.description}</p>}

                    {/* Answer type badge and guidance - Onboarding specific */}
                    {message.answerType && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <TooltipProvider>
                          <div className="inline-flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                              <AnswerTypeIcon type={message.answerType} />
                              {message.answerTypeLabel ?? message.answerType}
                            </span>
                            {message.answerGuidance && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="flex items-center text-muted-foreground transition-colors hover:text-muted-foreground"
                                    aria-label="Answer type guidance"
                                  >
                                    <HelpCircle className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  <p className="max-w-xs p-2 text-center text-sm text-muted-foreground">
                                    {message.answerGuidance}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      </div>
                    )}

                    {message.helperText && (
                      <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{message.helperText}</div>
                    )}

                    {message.files && message.files.length > 0 && (
                      <div className="mt-2">
                        {message.files.map((file) => (
                          <div key={file.id}>{renderFileLink(file)}</div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            </AiBubble>
          )}

          {message.sender === "user" && (
            <UserBubble>
              <>
                <div className="markdown-content">
                  <SafeMarkdown content={message.text || ""} />
                </div>

                {message.helperText && (
                  <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{message.helperText}</div>
                )}

                {message.files && message.files.length > 0 && (
                  <div className="mt-2">
                    {message.files.map((file) => (
                      <div
                        key={file.id}
                        className="text-xs"
                      >
                        {renderFileLink(file)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            </UserBubble>
          )}
        </div>
      );
    });

  return <div className={containerClassName}>{renderMessages()}</div>;
}
